import React, { useReducer } from "react";
import styled from "@emotion/styled";
import { Divider, TextareaAutosize } from "@mui/material";
import { Theme } from "@emotion/react";
import { borderRadius, cardShadow, fontSizes, media, SolidButton } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { commentInputReducer, createInitialCommentInputState, initialCommentInputState } from "./comment-input-state";
import { CommentImageComponent } from "./comment-image";
import { CommentImageUpload } from "./comment-image-upload";
import { CommentImage } from "./comment-types";

export const CommentInput = ({
    comment: existingComment,
    handleSave,
    handleCancel,
    className,
    saving,
}: {
    comment?: string;
    handleSave: (commentTextContent: string, commentImages: CommentImage[]) => void;
    handleCancel: () => void;
    className?: string;
    saving: boolean;
}) => {
    const translate = useIventisTranslate();

    const [state, dispatch] = useReducer(commentInputReducer, initialCommentInputState, () => createInitialCommentInputState(existingComment));

    const handleCommentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        dispatch({ type: "update_text", text: event.target.value });
    };

    const uploadFile = async (commentImage: CommentImage) => {
        dispatch({ type: "update_images", images: [commentImage] });
    };

    return (
        <StyledCommentWrapper className={className}>
            <StyledTextareaAutosize
                data-testid="comment-textarea"
                minRows={1}
                maxRows={6}
                placeholder={translate(Content.comments.commentForm.placeholder)}
                value={state.text}
                onChange={handleCommentChange}
                autoFocus
            />
            {state.images.map((image) =>
                typeof image === "string" ? (
                    <CommentImageComponent className="comment-image" key={image} imageId={image} fullScreenIcon={false} />
                ) : (
                    <CommentImageComponent className="comment-image" key={image.imageName} imageId={image.base64Image} fullScreenIcon={false} />
                )
            )}
            <Divider className="content-divider" />
            <StyledCommentFooter>
                <CommentImageUpload onFileUpload={uploadFile} disableImageIcon={state.doesCommentHaveExistingImage} replaceImageText={state.images.length > 0} />
                <StyledButtonContainer>
                    <StyledCancelButton data-testid="cancel-comment-button" variant="text" onClick={handleCancel}>
                        {translate(Content.common.buttons.cancel)}
                    </StyledCancelButton>
                    <StyledSaveButton
                        disabled={saving || (state.text.length < 1 && state.images.length < 1)}
                        data-testid="save-comment-button"
                        variant="contained"
                        onClick={() => handleSave(state.text, state.images)}
                    >
                        {saving && <FontAwesomeIcon className="save-icon" icon="circle-notch" spin />}
                        <span>{translate(Content.common.buttons.save)}</span>
                    </StyledSaveButton>
                </StyledButtonContainer>
            </StyledCommentFooter>
        </StyledCommentWrapper>
    );
};

const StyledCancelButton = styled(SolidButton)`
    display: none;
    width: 50%;
    ${media.medium} {
        display: block;
    }
`;

const StyledSaveButton = styled(SolidButton)`
    width: 50%;
`;

export const StyledCommentWrapper = styled.div`
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    ${media.medium} {
        max-width: 350px;
        box-shadow: ${cardShadow};
    }
    ${media.large} {
        max-width: 390px;
    }
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    background: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
    border-radius: ${borderRadius.input};

    .save-icon {
        position: absolute;
        left: 20px;
    }

    .content-divider {
        height: 2px;
        width: 100%;
    }

    .comment-image {
        margin: 10px;
    }
`;

const StyledTextareaAutosize = styled(TextareaAutosize)`
    font-size: ${fontSizes.small};
    background: none;
    box-sizing: border-box;
    resize: none;
    width: 100%;
    border: none;
    padding: 15px;

    &:focus {
        outline: none;
    }
`;

const StyledCommentFooter = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.625rem;
`;

const StyledButtonContainer = styled.div`
    display: flex;
    width: 75%;
    justify-content: flex-end;
`;
